import React, { useMemo, useState } from 'react';
import ActionButton from '../components/ActionButton';
import DataStatusBanner from '../components/DataStatusBanner';
import rentalFallback from '../data/rentalItems';
import useRemoteData from '../hooks/useRemoteData';
import { fetchRentalItems } from '../lib/api';
import { openSupportChat } from '../lib/supportChatConfig';

const RentalServicesPage = () => {
  const { data: rentalItems, loading, error } = useRemoteData(fetchRentalItems, rentalFallback, {
    preserveOnEmpty: true,
    cacheKey: 'rental-items',
    cacheStrategy: 'versioned',
  });
  const categories = useMemo(
    () => ['All', ...new Set(rentalItems.map((item) => item.category))],
    [rentalItems]
  );
  const [activeCategory, setActiveCategory] = useState('All');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);

  const filteredItems =
    activeCategory === 'All'
      ? rentalItems
      : rentalItems.filter((item) => item.category === activeCategory);

  const mobileCategoryButtons = categories.slice(0, 2);

  const openPreview = (item) => {
    setPreviewItem(item);
    setQuantity(1);
  };

  const closePreview = () => {
    setPreviewItem(null);
    setQuantity(1);
  };

  const contactRentalItem = (item, requestedQuantity = 1) => {
    openSupportChat({
      category: 'rental_enquiry',
      context: {
        source: 'rental_page',
        rental_item_id: item.id,
        rental_item_name: item.name,
        rental_item_category: item.category,
        requested_quantity: requestedQuantity,
      },
    });
  };

  return (
    <main className="rental-page">
      <section className="rental-hero" id="rental-rules">
        <div className="rental-hero-copy">
          <p className="eyebrow">Rules & support</p>
          <h2>Utensil rentals designed for stress-free events</h2>
          <p className="rental-hero-mobile-note">
            Reserve 48 hours before the event, report damage within 24 hours, and return
            everything within a day after your service.
          </p>
          <p className="rental-hero-desktop-detail">
            We inspect every kit before it leaves the kitchen, offer repair dispatch within 4 hours,
            and waive replacement fees when damage is reported immediately. Late returns incur
            a small fee that gets channeled back into sanitation and care for the next event.
          </p>
          <ActionButton
            variant="outline"
            size="xs"
            className="rental-hero-rules-btn"
            onClick={() => setRulesModalOpen(true)}
          >
            Read more on the rules
          </ActionButton>
          <ActionButton to="/contact" variant="primary" size="xs" className="rental-hero-contact-btn">
            Speak with our team
          </ActionButton>
        </div>
        <div className="rental-hero-card">
          <h3>How it works</h3>
          <ul>
            <li>Reserve your kits, confirm delivery, and receive sanitation reports.</li>
            <li>Broken gear is repaired or swapped within 4 hours—just report via texting the team.</li>
            <li>Return everything within 24 hours after your event and receive deposit clearance.</li>
          </ul>
          <div className="rental-hero-meta">
            <span>Repair window: 4 hours</span>
            <span>Return grace: 24 hours</span>
          </div>
        </div>
      </section>

      <section className="rental-catalog">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Equipment gallery</p>
            <h2>Choose the pieces you need</h2>
          </div>
        </div>

        <DataStatusBanner loading={loading} error={error} />

        <div className="rental-category-tabs">
          <div className="rental-category-desktop">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`menu-tab${activeCategory === category ? ' active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="rental-category-mobile">
            <div className="mobile-category-buttons">
              {mobileCategoryButtons.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`menu-tab${activeCategory === category ? ' active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="menu-dropdown-wrapper">
              <button
                type="button"
                className="menu-dropdown-trigger"
                onClick={() => setDropdownOpen((prev) => !prev)}
              >
                {activeCategory}
                <span aria-hidden="true">▾</span>
              </button>
              <ul className={`menu-dropdown-list${dropdownOpen ? ' open' : ''}`}>
                {categories.map((category) => (
                  <li key={category}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveCategory(category);
                        setDropdownOpen(false);
                      }}
                    >
                      {category}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="rental-card-grid">
          {filteredItems.map((item) => (
            <article key={item.id} className="rental-card">
              <div className="rental-card-image-wrapper" onClick={() => openPreview(item)}>
                <div className="rental-card-image">
                  <img src={item.image} alt={item.name} />
                </div>
                <button type="button" className="rental-card-mobile-overlay" onClick={() => openPreview(item)}>
                  Preview kit
                </button>
              </div>
              <div className="rental-card-body">
                <h3>{item.name}</h3>
                <p>{item.description}</p>
                <button type="button" className="rental-card-preview" onClick={() => openPreview(item)}>
                  Preview kit
                </button>
                <button
                  type="button"
                  className="rental-card-contact"
                  onClick={() => contactRentalItem(item)}
                >
                  Contact
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {previewItem && (
        <div className="rental-preview-overlay" onClick={closePreview}>
          <div className="rental-preview-dialog" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="rental-preview-close" onClick={closePreview}>
              ✕
            </button>
            <div className="rental-preview-image">
              <img src={previewItem.image} alt={previewItem.name} />
            </div>
            <div className="rental-preview-content">
              <h3>{previewItem.name}</h3>
              <p className="rental-preview-category">{previewItem.category}</p>
              <p>{previewItem.description}</p>
              <div className="rental-preview-price">₦{previewItem.price.toLocaleString()} / per set</div>
              <div className="rental-preview-quantity">
                <label htmlFor="quantity-input">Quantity:</label>
                <input
                  id="quantity-input"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <ActionButton
                variant="primary"
                size="xs"
                onClick={() => {
                  contactRentalItem(previewItem, quantity);
                  closePreview();
                }}
              >
                Contact about {quantity} set{quantity === 1 ? '' : 's'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
      {rulesModalOpen && (
        <div className="rental-rules-modal" onClick={() => setRulesModalOpen(false)}>
          <div className="rental-rules-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="rental-rules-close"
              onClick={() => setRulesModalOpen(false)}
            >
              ✕
            </button>
            <h3>Full rental procedures</h3>
            <ul>
              <li>Reserve your kits, confirm delivery timeline, and receive a sanitation report.</li>
              <li>Report broken gear instantly; we either repair on-site or swap within 4 hours.</li>
              <li>Return every piece within 24 hours after your event for a quick deposit release.</li>
            </ul>
            <p>
              Damage reported late may incur a replacement fee that goes back into maintaining the
              next set. Keep everything dry, clean, and stored safely when not in use.
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default RentalServicesPage;
